import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import * as z from 'zod';
import AuthContext from '../../../contexts/AuthContext';
import TokenListContext from '../../../contexts/TokenListContext';
import useAPI from '../../../hooks/useAPI';
import { formatCurrency } from '../../../utility/number.utils';
import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';

type BurnTokenModalProps = {
  open: boolean;
  onClose: (withSuccess?: boolean) => void;
  tokenId?: string | undefined;
  refetchTransactionHistory: () => void;
};

const BurnTokenModal = ({
  onClose,
  open,
  tokenId,
  refetchTransactionHistory,
}: BurnTokenModalProps) => {
  const { adminBalance } = useContext(AuthContext);

  type FormData = z.infer<typeof schema>;

  const schema = z.object({
    amount: z
      .string()
      .min(1, 'Total tokens must be greater than 0')
      .refine(val => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Total tokens must be a positive number greater than 0',
      })
      .refine(val => Number(val) <= (adminBalance?.totalBalance ?? 0), {
        message:
          'Requested number of tokens to burn exceeds the available tokens',
      }),
  });

  const [formData, setFormData] = useState({
    amount: '',
    address: '',
  });
  const [showSuccess, setShow] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>();
  const { refetchTransactionData, tokenList } = useContext(TokenListContext);

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const {
    refetch: burnToken,
    data,
    isLoading,
  } = useAPI({
    url: '/token/burn/cashout',
    method: 'POST',
    onSuccess: data => {
      setShow(true);
      setTransactionId(data.result.data.data.transactionId);
      refetchTransactionData();
      refetchTransactionHistory && refetchTransactionHistory();
      reset();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const handleBurnToken = (data: FormData) => {
    const formDataToSend = new FormData();

    formDataToSend.append('amount', data.amount);
    const payload = {
      amount: data.amount,
      tokenId: tokenId,
    };

    if (!isLoading) burnToken(payload);
  };

  if (open && showSuccess)
    return (
      <TransactionSuccessModal
        open={showSuccess}
        title="Token burnt successfully"
        isCloseIconVisible={true}
        onClose={() => {
          setShow(false);
          onClose(true);
        }}
      />
    );

  return (
    <Modal
      open={open}
      title="Burn Token"
      onClose={onClose}
      dialogClassName={styles.dialog_content}
      isCloseIconVisible={!isLoading}
    >
      <form onSubmit={handleSubmit(handleBurnToken)}>
        <div className={styles.receiveWrapper}>
          <div className={styles.receiveInner}>
            <div className={styles.receiveItem}>
              <label>Token Name</label>
              <span>
                {tokenList?.[0]?.tokenName || '-'}(
                {tokenList?.[0]?.tokenSymbol || '-'})
              </span>
            </div>
            <div className={styles.receiveItem}>
              <label>Available Tokens</label>
              <span>
                {formatCurrency(adminBalance?.UserBalance ?? 0, {
                  currency: '',
                  decimals: 0,
                })}
              </span>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="" className={styles.label}>
              Enter Amount <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={0}
                    allowNegative={false}
                    customInput={Input}
                    onValueChange={value => field.onChange(value.value)}
                    error={errors.amount?.message as string}
                    style={{
                      paddingRight: tokenList?.[0]?.tokenSymbol
                        ? '60px'
                        : '10px',
                    }}
                  />
                  {tokenList?.[0]?.tokenSymbol && (
                    <span
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontWeight: 600,
                        pointerEvents: 'none',
                      }}
                    >
                      {tokenList[0].tokenSymbol}
                    </span>
                  )}
                </div>
              )}
            />
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button
            disabled={isLoading}
            variant="outline"
            onClick={() => onClose()}
            type="button"
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            variant="dark"
            type="submit"
            className={styles.delete_btn}
          >
            {isLoading ? <Loader /> : 'Confirm'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BurnTokenModal;
